import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import ThemeSwitcher from './ThemeSwitcher';
import ReportSearch from './ReportSearch';
import DropdownMenu from './DropdownMenu';
import { ThemeContext } from '../Context';
import { ReactComponent as BlueNoteLogo } from '../../assets/logo_bluenote.svg';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  DialogContentText, 
  Typography 
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

function Header() {
  const { theme } = useContext(ThemeContext);
  const [isPilot, setIsPilot] = useState(false);
  const [isPilotDialogOpen, setIsPilotDialogOpen] = useState(false);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const isDark = theme === 'dark';

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    // Observer les changements de classe sur le body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen(); // Vérification initiale

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchIsPilot = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (!mounted) return;
        setIsPilot(Boolean(response?.data?.isPilot));
      } catch (error) {
        console.error('Erreur lors de la récupération de isPilot :', error);
      }
    };

    fetchIsPilot();
    return () => {
      mounted = false;
    };
  }, []);

  const handlePilotFeedback = () => {
    try {
      window.open('https://pyll.be', '_blank', 'noopener');
    } catch (error) {
      console.error('Impossible d\'ouvrir le formulaire de feedback :', error);
    }
  };
  
  // Ne pas afficher le header quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  return (
    <Box 
      className={theme}
      sx={{
        height: '6.5vh',
        minHeight: '48px',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        padding: '0 18px',
        background: isDark 
          ? 'linear-gradient(to right,rgb(37, 53, 100),rgb(52, 74, 141))'
          : 'linear-gradient(to right,#1a253c, #2d407a)',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1200
      }}
    >
      <Box 
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          position: 'relative', // Ajouté pour le positionnement absolu de la section centrale
          overflow: 'hidden'
        }}
      >
        {/* Left Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flexShrink: 0,
          zIndex: 1 // Pour s'assurer que cette section reste au-dessus
        }}>
          <BlueNoteLogo
            className='logo-bluenote'
            style={{
              height: '42px',
              flexShrink: 0,
              width: 'auto',
              color: 'white'
            }}
          />
          <h1 style={{ color: 'white', fontSize: '2rem', margin: 0 }}>BlueNote</h1>
          <p style={{ color: 'white', fontStyle: 'italic', fontSize: '0.95rem', margin: 0 }}><SettingsFieldDisplay fieldName="clientName" /></p>
        </Box>
        
        {/* Center Section - Positionnement absolu au centre */}
        <Box sx={{ 
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'auto',
          maxWidth: '50%', // Limite la largeur pour éviter de déborder
          zIndex: 0 // Pour permettre aux sections latérales de passer au-dessus si nécessaire
        }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            {isPilot && (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setIsPilotDialogOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsPilotDialogOpen(true);
                  }
                }}
                sx={{
                  backgroundColor: '#b71c1c',
                  color: '#ffffff',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 0.5,
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  lineHeight: 1.15,
                  cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25)',
                  position: 'absolute',
                  right: 'calc(100% + 10px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  whiteSpace: 'nowrap',
                  zIndex: 2,
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: '1.2rem', color: '#ffffff' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                  <Box component="div" sx={{ fontSize: '0.9rem' }}>Version Pilote</Box>
                  <Box component="div" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Avis, retour, bug ?</Box>
                </Box>
              </Box>
            )}
            <ReportSearch />
          </Box>
        </Box>
        
        {/* Right Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flexShrink: 0,
          marginLeft: 'auto', // Pousse cette section à droite
          zIndex: 1 // Pour s'assurer que cette section reste au-dessus
        }}>
          <DropdownMenu />
          <ThemeSwitcher />
        </Box>
      </Box>

      <Dialog
        open={isPilotDialogOpen}
        onClose={() => setIsPilotDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Version pilote</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              BlueNote fonctionne actuellement en mode pilote pour votre zone.
            </Typography>
            <Typography variant="body2">
            BlueNote est actuellement déployé en version pilote pour votre zone, dans une logique d’amélioration continue.
            <br />
            Les contenus générés doivent toujours être relus et validés par l’utilisateur, en particulier ceux issus de l’assistant d'intelligence artificielle « Guardia », utilisé comme aide à la rédaction.
            <br />
            Nous vous invitons à signaler les fautes d’orthographe, options manquantes dans une rubrique ou anomalies de génération via le formulaire de feedback.
            <br />
            Chaque retour contribue à la stabilisation de la version finale.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handlePilotFeedback}
          >
            Formulaire de feedback
          </Button>
          <Button onClick={() => setIsPilotDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Header;